<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { useRoomStore } from '@/stores/modules/room'
import RuneRuinMonsterDetail from '@/components/rune-ruin-monster-detail/RuneRuinMonsterDetail.vue'
import { soulClass } from '@/views/awaken/data'
import { RuneRoleText } from '@/views/item/data'
import { runeRuinRank } from '../data'

const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ runeRuinMonster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'giveUp', 'showRoom'])

const isLowDiff = ref(false)
function handleHuntRuneRuinMonster() {
  if (userStore.runeRuinTimes <= 0) return ElMessage.warning('无奖励时无法进行探索')
  if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法进行探索')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">探索</span>【<span class="awaken">符石遗迹</span>】吗？${
      isLowDiff.value ? '<br />【您开启了<span class="info">低难度</span>选项，本次探索的遗迹光泽必定为<span class="normal">微弱</span>】' : ''
    }</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntRuneRuinMonster', data: { lowDiff: isLowDiff.value } })
    emits('getMonster', res)
    ElMessage.success('探索成功')
  })
}
async function handleStartRuneRuin() {
  if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法进行战斗')
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="pet">符石遗迹</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startRuneRuinFight' })
    roomShow.value = false
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'runeRuin' })
  }
}
function handleRefreshRuneRuin() {
  if (isLowDiff.value && userStore.gold < 50000) return ElMessage.warning('重新探索需要 50,000金币')
  else if (!isLowDiff.value && userStore.gold < 100000) return ElMessage.warning('重新探索需要 100,000金币')

  if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法进行战斗')
  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探索魔物')
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="hp">放弃</span>探索当前遗迹并进行重新<span class="info">探索</span>吗？（消耗 <span class="gold">${
      isLowDiff.value ? '50,000' : '100,000'
    }</span>金币）${
      isLowDiff.value ? '<br />【您开启了<span class="info">低难度</span>选项，本次探索的遗迹光泽必定为<span class="normal">微弱</span>】' : ''
    }）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshRuneRuinMonster', data: { lowDiff: isLowDiff.value } })
    if (isLowDiff.value) {
      userStore.gold -= 50000
    } else {
      userStore.gold -= 100000
    }
    emits('getMonster', res)
    ElMessage.success('重新探索成功')
  })
}

const roomShow = ref(false)
function handleOpenRoom() {
  if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法进行战斗')
  emits('showRoom')
}
</script>

<template>
  <div class="border-wrap rune-ruin">
    <h4 class="awaken">
      符石遗迹
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>符石遗迹说明</h4>
            <p>1. 符石遗迹<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 通过<span class="info">探索</span>，您会随机遭遇一只<span class="info">符石尖塔怪物</span>，同时窥探到遗迹的<span class="info"
                >光泽</span
              >及各项信息。
            </p>
            <p>
              3. 通过符石遗迹战斗您将必定获得对应的<span class="gold">赏金</span>、对应<span class="info">战魂倾向</span>的 2块<span class="awaken"
                >符石</span
              >，以及 2<span class="awaken">符石碎星</span>。
            </p>
            <p>
              4. 符石遗迹支持<span class="info">双人组队</span>，当组队挑战时，怪物的血量增加<span class="grow">0.8</span
              >倍，同时还会略微增加符石的品质爆率，组队挑战时无法中途取消挑战。
            </p>
            <p>5. 战斗时支持<span class="info">切换装备即刻生效</span>，当输出不足时，请及时更换装备。</p>
            <p>6. 战斗失败时，<span class="grow">不会消耗</span>奖励次数；当奖励次数用尽时，您无法进行探索及获得奖励，但您仍可以帮助其他玩家战斗。</p>
            <p>
              7. <span class="info">光泽</span>越闪亮的怪物挑战难度越大，同时<span class="gold">赏金</span>也将更丰厚，若难以攻克可尝试<span
                class="grow"
                >组队挑战</span
              >，或选择开启<span class="info">低难度</span>选项后再进行探索。
            </p>
            <p>
              8. 开启<span class="info">低难度</span>选项时<span class="grow">不会降低奖励</span>，但探索的<span class="info">光泽</span>必定为<span
                class="normal"
                >微弱</span
              >，同时降低重新探索花费至<span class="gold">50,000</span>金币。
            </p>
            <p>
              9. 符石品质爆率：<span class="normal">普通</span> 40%，<span class="race">稀有</span> 50%，<span class="epic">史诗</span>
              10%（组队战斗时增加2%的<span class="epic">史诗</span>爆率；<span class="legend">闪耀</span>光泽时，增加5%的<span class="race">稀有</span
              >爆率，3%的<span class="epic">史诗</span>爆率）
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center"><span>低难度：</span><el-switch v-model="isLowDiff" size="small" /></p>
    <p>剩余次数：{{ userStore.runeRuinTimes }}</p>
    <p>
      当前怪物：
      <RuneRuinMonsterDetail :monster="runeRuinMonster" />
    </p>
    <p v-if="runeRuinMonster">
      <span
        >光泽：<span :class="runeRuinRank[runeRuinMonster.rank]?.class">{{ runeRuinRank[runeRuinMonster.rank]?.name }}</span></span
      >
      <span style="margin-left: 30px"
        >倾向：<span :class="soulClass[runeRuinMonster.soulType]">{{ RuneRoleText[runeRuinMonster.soulType] }}</span></span
      >
    </p>
    <div style="margin: 10px 0">
      <div v-show="!runeRuinMonster?.name">
        <el-button type="warning" plain @click="handleHuntRuneRuinMonster" :disabled="!userStore.soulType">
          {{ !userStore.soulType ? '觉醒后解锁' : '探索' }}
        </el-button>
        <el-button type="primary" plain :disabled="!userStore.soulType" @click="handleOpenRoom">组队战斗</el-button>
      </div>
      <div v-show="runeRuinMonster?.name">
        <el-button type="success" plain @click="() => handleStartRuneRuin()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshRuneRuin">重新探索</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
